import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../redux-store";
import { ClipCardProps } from "../../Components/Cards/ClipCard/clip-card";
import { QCMarkerCardProps } from "../../Components/Cards/QCMarkerCard/qc-marker-card";
import { stat } from "fs";
import { ReactPlayerMarker } from "./redux-slice-react-player";

export enum TimelineType {
  Clips = "clips",
  Markers = "markers",
  Storyboard = "storyboard",
  Transcript = "transcript",
}

export type EditClipProps = {
  id: number;
  clipName: string;
  clipDescription: string;
  createdBy?: string;
  timecodes: {
    in: ReactPlayerMarker;
    out: ReactPlayerMarker;
  };
};

export interface ReduxStateTimeline {
  activeTimeline: TimelineType | null;
  timelineLoading: boolean;
  creatingOrEditingTimelineEvent: boolean;
  timelineEventBeingEdited: ClipCardProps | null;
  timelineClipItems: { default: ClipCardProps[]; filtered: ClipCardProps[]; focused: ClipCardProps | null };
  timelineMarkerItems: { default: QCMarkerCardProps[]; filtered: QCMarkerCardProps[] };
  timelineStoryboardItems: QCMarkerCardProps[];
  timelineTranscriptItems: QCMarkerCardProps[];
}

const initialState: ReduxStateTimeline = {
  activeTimeline: null,
  timelineLoading: false,
  creatingOrEditingTimelineEvent: false,
  timelineEventBeingEdited: null,
  timelineClipItems: {
    default: [],
    filtered: [],
    focused: null,
  },
  timelineMarkerItems: {
    default: [],
    filtered: [],
  },
  timelineStoryboardItems: [],
  timelineTranscriptItems: [],
};

const timelineSlice = createSlice({
  name: "timeline",
  initialState,
  reducers: {
    InitState: (state, action) => {
      return initialState;
    },
    SetTimelineLoading: (state, action: PayloadAction<boolean>) => {
      state.timelineLoading = action.payload;
    },
    SetActiveTimeline: (state, action: PayloadAction<TimelineType | null>) => {
      state.activeTimeline = action.payload;
    },
    SetCreatingOrEditingTimelineEvent: (
      state,
      action: PayloadAction<{ creatingOrEditing: boolean; event?: ClipCardProps }>
    ) => {
      // Reducer handles toggling timeline controls as well as edit / create mode by passing the timelineEventBeingEdited
      state.creatingOrEditingTimelineEvent = action.payload.creatingOrEditing;
      state.timelineEventBeingEdited = action.payload.event || null;
    },
    SetTimelineClips: (state, action: PayloadAction<ClipCardProps[]>) => {
      state.timelineClipItems.default = action.payload as ClipCardProps[];
    },
    SetFocusedTimelineClip: (state, action: PayloadAction<ClipCardProps | null>) => {
      state.timelineClipItems.focused = action.payload as ClipCardProps;
    },
    SetFilteredTimelineClips: (state, action: PayloadAction<ClipCardProps[]>) => {
      state.timelineClipItems.filtered = action.payload as ClipCardProps[];
    },
    SetTimelineMarkers: (state, action: PayloadAction<QCMarkerCardProps[]>) => {
      state.timelineMarkerItems.default = action.payload as QCMarkerCardProps[];
    },
    SetFilteredTimelineMarkers: (state, action: PayloadAction<QCMarkerCardProps[]>) => {
      state.timelineMarkerItems.filtered = action.payload as QCMarkerCardProps[];
    },
    SetTimelineStoryboard: (state, action: PayloadAction<QCMarkerCardProps[]>) => {
      state.timelineStoryboardItems = action.payload as QCMarkerCardProps[];
    },
    SetTimelineTranscript: (state, action: PayloadAction<QCMarkerCardProps[]>) => {
      state.timelineTranscriptItems = action.payload as QCMarkerCardProps[];
    },
  },
});

export const {
  SetActiveTimeline,
  SetTimelineLoading,
  SetCreatingOrEditingTimelineEvent,
  //   SetTimelineEventBeingEdited,
  SetTimelineClips,
  SetFocusedTimelineClip,
  SetFilteredTimelineClips,
  SetTimelineMarkers,
  SetFilteredTimelineMarkers,
  SetTimelineStoryboard,
  SetTimelineTranscript,
} = timelineSlice.actions;

export const GetTimelineLoading = (state: RootState) => state.timeline.timelineLoading;

export const GetTimelineMarkers = (state: RootState) => state.timeline.timelineMarkerItems.default;
export const GetFilteredTimelineMarkers = (state: RootState) => state.timeline.timelineMarkerItems.filtered;

export const GetTimelineStoryboard = (state: RootState) => state.timeline.timelineStoryboardItems;

export const GetTimelineClips = (state: RootState) => state.timeline.timelineClipItems.default;
export const GetFilteredTimelineClips = (state: RootState) => state.timeline.timelineClipItems.filtered;
export const GetFocusedTimelineClip = (state: RootState) => state.timeline.timelineClipItems.focused;

export const GetActiveTimeline = (state: RootState) => state.timeline.activeTimeline;
export const GetCreatingOrEditingTimelineEvent = (state: RootState) => state.timeline.creatingOrEditingTimelineEvent;
export const GetTimelineEventBeingEdited = (state: RootState) => state.timeline.timelineEventBeingEdited;

export const GetTimelineTranscriptItems = (state: RootState) => state.timeline.timelineTranscriptItems;

export default timelineSlice.reducer;
